import { isNotEmptyTipTap } from '@poseidon/components/utils';

import type { SequenceStep, SequenceSwitchAttribute, SequenceSwitchBranch } from '~/components/Sequence/sequenceSteps';

/**
 * A lossless, canonical **text + config** form for a sequence — bijective with the (canonical)
 * `SequenceStep[]` graph. Proven by the fuzz round-trip in sequenceText.vitest.ts:
 *   parse(serialize(g))         deep-equals g          (graph → text → graph)
 *   serialize(parse(t))         equals     t           (canonical text → graph → text)
 *
 * The `text` carries the topology + structural scalars; every **content-bearing** step is a labelled ref
 * (`M1`, `E2`, …) whose rich payload lives in `config` — so message/subject HTML never has to be escaped
 * into the text (which was the fragile part), and the text stays readable:
 *
 *   text:   "(F, L3) -> W1d -> C -> A5 -> M1 -> ?AUM{ 0..1000000: E1, *: F }"
 *   config: { M1: { body: "<p>Hi {{first}}</p>" }, E1: { subject: "…", body: "…" } }
 *
 * Grammar of `text` (canonical output; parse also accepts a redundant `d` on waits + extra whitespace):
 *   seq    = step ( " -> " step )*
 *   step   = F | X | L[n] | W<days>[w] | A[<days>][{ seq }]     // scalars inline
 *          | M<n> | R<n> | I<n> | E<n>                          // required-content: labelled ref
 *          | C[<n>] | P[<n>]                                    // optional-content: bare, or labelled ref
 *          | "(" step ("," step)* ")"   // parallel group (single-step arms, ≥2)
 *          | "[" seq "]"                 // spawn (one detached arm, ≥1)
 *          | "?" ("AUM"|"AGE") "{" branch ("," branch)* "}"   // switch (top-level, terminal)
 *   branch = ( <n>?".."<n>? | "*" ) ":" [ seq ]
 *
 * Canonicalization (one graph ↔ one text+config): default fields are dropped — `likeCount:1`,
 * `deadlineDays:5`, `replyToPrevious:true`, `isPersonalized:false`, `waitUnit:'days'`, empty optional notes,
 * and the vestigial switch-branch `label` (always null). Labels are assigned per-letter in document order.
 */

const DEADLINE_DEFAULT = 5;
const LIKE_DEFAULT = 1;

/** The per-step payload held out-of-band, keyed by the step's text label (`M1`, `E2`, …). */
export interface StepConfig {
  body?: string;
  subject?: string;
  replyToPrevious?: boolean;
  isPersonalized?: boolean;
}
export interface SequenceText {
  text: string;
  config: Record<string, StepConfig>;
}

// ── serialize ──────────────────────────────────────────────────────────────
export function serialize(steps: SequenceStep[]): SequenceText {
  const config: Record<string, StepConfig> = {};
  const counters: Record<string, number> = {};
  const nextLabel = (letter: string): string => `${letter}${(counters[letter] = (counters[letter] ?? 0) + 1)}`;

  const seq = (list: SequenceStep[]): string => list.map(step).join(' -> ');

  // Always-labelled content step: reserve a label, store its config (+ isPersonalized if set).
  const emit = (letter: string, base: StepConfig, s: SequenceStep): string => {
    const lbl = nextLabel(letter);
    config[lbl] = { ...base, ...(s.isPersonalized === true ? { isPersonalized: true } : {}) };
    return lbl;
  };
  // Optional-content step (connect/call): bare letter unless it carries a note or a flag.
  const emitOptional = (letter: string, s: SequenceStep): string => {
    const note = s.body != null && isNotEmptyTipTap(s.body) ? s.body : undefined;
    const isP = s.isPersonalized === true;
    if (note == null && !isP) return letter;
    const lbl = nextLabel(letter);
    config[lbl] = { ...(note != null ? { body: note } : {}), ...(isP ? { isPersonalized: true } : {}) };
    return lbl;
  };
  const branch = (b: SequenceSwitchBranch): string => {
    const range = b.isCatchAll ? '*' : `${b.minValue ?? ''}..${b.maxValue ?? ''}`;
    return b.arm.length > 0 ? `${range}: ${seq(b.arm)}` : `${range}:`;
  };

  const step = (s: SequenceStep): string => {
    switch (s.type) {
      case 'follow':
        return 'F';
      case 'withdraw':
        return 'X';
      case 'like':
        return s.likeCount != null && s.likeCount !== LIKE_DEFAULT ? `L${s.likeCount}` : 'L';
      case 'wait':
        return `W${s.waitDays ?? 1}${s.waitUnit === 'weeks' ? 'w' : ''}`;
      case 'await_connect': {
        const d = s.deadlineDays != null && s.deadlineDays !== DEADLINE_DEFAULT ? String(s.deadlineDays) : '';
        const arm = s.branches?.notAccepted;
        return `A${d}${arm != null && arm.length > 0 ? `{ ${seq(arm)} }` : ''}`;
      }
      case 'message':
        return emit('M', { body: s.body ?? '' }, s);
      case 'reminder':
        return emit('R', { body: s.body ?? '' }, s);
      case 'inmail':
        return emit('I', { subject: s.subject ?? '', body: s.body ?? '' }, s);
      case 'email':
        return emit('E', { subject: s.subject ?? '', body: s.body ?? '', ...(s.replyToPrevious === false ? { replyToPrevious: false } : {}) }, s);
      case 'connect':
        return emitOptional('C', s);
      case 'call':
        return emitOptional('P', s);
      case 'group':
        return `(${(s.arms ?? []).map((a) => step(a[0]!)).join(', ')})`;
      case 'spawn':
        return `[${seq(s.arm ?? [])}]`;
      case 'switch': {
        const sw = s.switch!;
        return `?${sw.attribute === 'aum' ? 'AUM' : 'AGE'}{ ${sw.branches.map(branch).join(', ')} }`;
      }
      default:
        throw new Error(`serialize: unhandled step type ${JSON.stringify(s.type)}`);
    }
  };

  return { text: seq(steps), config };
}

// ── parse ──────────────────────────────────────────────────────────────────
export function parse(t: SequenceText): SequenceStep[] {
  const r = new Reader(t.text, t.config);
  r.ws();
  if (r.eof()) return []; // an empty sequence serializes to "" — round-trips back to []
  const seq = r.seq();
  r.ws();
  if (!r.eof()) throw new Error(`parse: trailing input at ${r.i}: ${JSON.stringify(t.text.slice(r.i))}`);
  return seq;
}

class Reader {
  i = 0;
  constructor(
    readonly s: string,
    readonly config: Record<string, StepConfig>,
  ) {}
  eof(): boolean {
    return this.i >= this.s.length;
  }
  ws(): void {
    while (this.i < this.s.length && /\s/.test(this.s[this.i]!)) this.i++;
  }
  private char(): string | undefined {
    return this.s[this.i];
  }
  private at(str: string): boolean {
    this.ws();
    return this.s.startsWith(str, this.i);
  }
  private take(str: string): void {
    this.ws();
    if (!this.s.startsWith(str, this.i)) throw new Error(`parse: expected ${JSON.stringify(str)} at ${this.i}`);
    this.i += str.length;
  }
  /** Digits at the current position, NO whitespace skip (a step's numeric suffix binds tight). */
  private digitsTight(): number | null {
    const m = /^[0-9]+/.exec(this.s.slice(this.i));
    if (m == null) return null;
    this.i += m[0].length;
    return Number(m[0]);
  }
  private digitsWs(): number | null {
    this.ws();
    return this.digitsTight();
  }
  private cfg(label: string): StepConfig {
    return this.config[label] ?? {};
  }

  seq(): SequenceStep[] {
    const steps = [this.step()];
    while (this.at('->')) {
      this.take('->');
      steps.push(this.step());
    }
    return steps;
  }

  step(): SequenceStep {
    this.ws();
    const c = this.char();
    if (c === '(') return this.group();
    if (c === '[') return this.spawn();
    if (c === '?') return this.switchStep();
    this.i++; // consume the step letter
    switch (c) {
      case 'F':
        return { type: 'follow' };
      case 'X':
        return { type: 'withdraw' };
      case 'L': {
        const n = this.digitsTight();
        return n != null ? { type: 'like', likeCount: n } : { type: 'like' };
      }
      case 'W': {
        const days = this.digitsTight();
        if (days == null) throw new Error(`parse: wait needs a duration at ${this.i}`);
        const u = this.char();
        if (u === 'd') this.i++; // redundant days marker — accepted; canonical omits it
        else if (u === 'w') {
          this.i++;
          return { type: 'wait', waitDays: days, waitUnit: 'weeks' };
        }
        return { type: 'wait', waitDays: days };
      }
      case 'A': {
        const d = this.digitsTight();
        let notAccepted: SequenceStep[] | undefined;
        if (this.at('{')) {
          this.take('{');
          notAccepted = this.at('}') ? [] : this.seq();
          this.take('}');
        }
        return {
          type: 'await_connect',
          ...(d != null ? { deadlineDays: d } : {}),
          ...(notAccepted != null && notAccepted.length > 0 ? { branches: { notAccepted } } : {}),
        };
      }
      case 'M':
        return this.contentStep('message', 'M');
      case 'R':
        return this.contentStep('reminder', 'R');
      case 'I':
        return this.contentStep('inmail', 'I');
      case 'E':
        return this.contentStep('email', 'E');
      case 'C':
        return this.optionalStep('connect', 'C');
      case 'P':
        return this.optionalStep('call', 'P');
      default:
        throw new Error(`parse: unknown step ${JSON.stringify(c)} at ${this.i - 1}`);
    }
  }

  /** A required-content step — its label index is mandatory; fields come from config. */
  private contentStep(type: 'message' | 'reminder' | 'inmail' | 'email', letter: string): SequenceStep {
    const n = this.digitsTight();
    if (n == null) throw new Error(`parse: ${letter} needs a config label at ${this.i}`);
    const cfg = this.cfg(`${letter}${n}`);
    const isP = cfg.isPersonalized === true ? { isPersonalized: true as const } : {};
    if (type === 'inmail') return { type, subject: cfg.subject ?? '', body: cfg.body ?? '', ...isP };
    if (type === 'email')
      return {
        type,
        subject: cfg.subject ?? '',
        body: cfg.body ?? '',
        ...(cfg.replyToPrevious === false ? { replyToPrevious: false as const } : {}),
        ...isP,
      };
    return { type, body: cfg.body ?? '', ...isP };
  }
  /** An optional-content step — bare letter, or a label whose config carries the note / flag. */
  private optionalStep(type: 'connect' | 'call', letter: string): SequenceStep {
    const n = this.digitsTight();
    if (n == null) return { type };
    const cfg = this.cfg(`${letter}${n}`);
    return {
      type,
      ...(cfg.body != null ? { body: cfg.body } : {}),
      ...(cfg.isPersonalized === true ? { isPersonalized: true as const } : {}),
    };
  }

  private singleStep(): SequenceStep {
    const s = this.step();
    if (s.type === 'group' || s.type === 'spawn' || s.type === 'switch')
      throw new Error('parse: a group arm must be a single, non-container step');
    return s;
  }
  private group(): SequenceStep {
    this.take('(');
    const arms: SequenceStep[][] = [[this.singleStep()]];
    while (this.at(',')) {
      this.take(',');
      arms.push([this.singleStep()]);
    }
    this.take(')');
    if (arms.length < 2) throw new Error('parse: a parallel group needs ≥2 arms');
    return { type: 'group', join: 'all', arms };
  }
  private spawn(): SequenceStep {
    this.take('[');
    const arm = this.seq();
    this.take(']');
    if (arm.length === 0) throw new Error('parse: a spawn needs ≥1 step');
    return { type: 'spawn', arm };
  }
  private switchStep(): SequenceStep {
    this.take('?');
    this.ws();
    let attribute: SequenceSwitchAttribute;
    if (this.s.startsWith('AUM', this.i)) {
      attribute = 'aum';
      this.i += 3;
    } else if (this.s.startsWith('AGE', this.i)) {
      attribute = 'age';
      this.i += 3;
    } else throw new Error(`parse: expected AUM or AGE at ${this.i}`);
    this.take('{');
    const branches: SequenceSwitchBranch[] = [this.branch()];
    while (this.at(',')) {
      this.take(',');
      branches.push(this.branch());
    }
    this.take('}');
    return { type: 'switch', switch: { attribute, branches } };
  }
  private branch(): SequenceSwitchBranch {
    this.ws();
    let minValue: number | null = null;
    let maxValue: number | null = null;
    let isCatchAll = false;
    if (this.char() === '*') {
      this.i++;
      isCatchAll = true;
    } else {
      minValue = this.digitsWs();
      this.take('..');
      maxValue = this.digitsWs();
    }
    this.take(':');
    this.ws();
    const arm = this.char() === ',' || this.char() === '}' ? [] : this.seq();
    return { minValue, maxValue, label: null, isCatchAll, arm };
  }
}
